/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';
import error from '../../../../common/nodejs/errors';
import util from '../../../../share/util/utils';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import { REQUEST_STATUS_COMPLETED } from '../../../../share/util/api/polling';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  EXECUTION: 'execution',
  SUCCESS: 'success',
  FAILED: 'failed',
  ERROR: 'error'
};

/** 現在セッション */
const SESSION_PROPERTY = {
  DEFAULT: 0,
  NON_DEFAULT: 1
};

export default {
  name: 'requirement156',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      sessionProperty: SESSION_PROPERTY.DEFAULT,
      modal_requirement_156: false,
      isWriteSuccess: false,
      monitoringIntervalTime: 0,
      readValue: '',
      exitPromise: null,
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
        case PHASE.ERROR:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.EXECUTION:
        case PHASE.START:
        default:
          return 'secondary';
      }
    },

    /**
     * ダイアログのヘッダー文言
     */
    headerTitle() {
      // 待機
      const startTitle = 'message.common_title_confirmation';
      // 実行中
      const ExecutionTitle = 'individual.common_title_execution';
      // 正常終了
      const SuccessTitle = 'message.common_title_success';
      // 中止失敗, 共通エラー
      const ErrorTitle = 'message.common_title_error';
      const titleMap = {
        start: startTitle,
        execution: ExecutionTitle,
        success: SuccessTitle,
        error: ErrorTitle,
        failed: ErrorTitle
      };
      return this.$t(titleMap[this.phase]);
    },

    /** 表示メッセージ */
    workSupportMessage() {
      const messageMap = {
        start: `${this.i18nWsKey}.REQ_WS_156_TXT_1`,
        execution: `${this.i18nWsKey}.REQ_WS_156_TXT_2`,
        success: `${this.i18nWsKey}.REQ_WS_156_TXT_3`,
        failed: `${this.i18nWsKey}.REQ_WS_156_TXT_4`,
        error: `individual.error_communication_error`
      };
      if (this.phase === PHASE.FAILED) {
        return this.$t(messageMap[this.phase]).replace('$DID$', this.readValue);
      }
      return this.$t(messageMap[this.phase]);
    },

    // 「OK」ボタンの表示確認
    showButtonOk() {
      const showOkButtonMap = {
        start: true,
        success: true,
        failed: true,
        error: true
      };
      let result = showOkButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },

    // 「キャンセル」ボタンの表示判定
    showButtonCancel() {
      const showCancelButtonMap = {
        start: true,
      };
      let result = showCancelButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.sessionProperty = SESSION_PROPERTY.DEFAULT;
      this.modal_requirement_156 = true;
      this.isWriteSuccess = false;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_156 = false;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     * @param {string} calledFrom 呼び出し元
     */
    async validateErrorResult(result, calledFrom = '') {
      await apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        async (result) => {
          result;
          // terminate,disposeから呼ばれた場合は共通エラーに遷移しない
          if (calledFrom === 'terminate' || calledFrom === 'dispose') return;
          await this.showUnexpectedError();
        },
      );
    },

    /** その他エラー発生時の表示 */
    async showUnexpectedError() {
      // スピナー消えてしまうのを防ぐためPromiseを保持し、各API失敗時に解決を待つ
      this.exitPromise = this.sessionProperty === SESSION_PROPERTY.NON_DEFAULT ? this.terminate() : this.dispose();
      await this.exitPromise;
      // 異常終了：エラー表示
      this.phase = PHASE.ERROR;
    },

    /** 「OK」ボタンの押下処理 */
    onOkClicked() {
      switch (this.phase) {
        case 'start':
          this.onConfirmOkClicked();
          break;
        case 'success':
        case 'failed':
          this.exit();
          break;
        case 'error':
          this.closeWizard();
          break;
        default:
          break;
      }
    },

    /** 「キャンセル」ボタンの押下処理 */
    onCancelClicked() {
      this.closeWizard();
    },

    /** 待機画面「OK」押下 */
    async onConfirmOkClicked() {
      // データ書込み処理
      this.write();
    },

    /** データ書込み処理 */
    async write() {
      try {
        this.isLoading = true;
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();

        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'write'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          async (errorType) => { errorType; await this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          async (result) => { await this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          async (result) => { await this.validateErrorResult(result); },
        );

        if (!response) {
          // pollingWspApiはエラーコールバックの終了を待たずnullを返す
          // 非同期処理を含んでいるshowUnexpectedErrorが呼ばれた場合、スピナー消えてしまうためPromiseの解決を待つ
          if (this.exitPromise) await this.exitPromise;
          return;
        }

        // 正常コールバック
        if (response.data?.status === REQUEST_STATUS_COMPLETED) {
          // Session遷移用フラグは、デフォルトのまま false とする。
          this.sessionProperty = SESSION_PROPERTY.DEFAULT;
          // 監視間隔(monitoring_interval_time)を取得
          this.monitoringIntervalTime = response.data.user_data?.monitoring_interval_time ? response.data.user_data.monitoring_interval_time : 0;
          // データ書込み処理成功フラグを true にする。
          this.isWriteSuccess = true;
          // DefaultSession遷移処理に遷移
          await this.terminate();
        } else {
          // NonDefaultSession遷移が実行済み
          if (response.data?.user_data?.is_session) {
            this.sessionProperty = SESSION_PROPERTY.NON_DEFAULT;
          }
          // 共通エラー画面へ遷移
          await this.showUnexpectedError();
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** DefaultSession遷移（データ書込み成功後、共通エラー） */
    async terminate() {
      try {
        this.isLoading = true;
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();

        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'terminate'
          },
          // エラー発生のコールバックを実装
          undefined,
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          async (result) => { await this.validateErrorResult(result, 'terminate'); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          async (result) => { await this.validateErrorResult(result, 'terminate'); },
        );

        if (response) {
          if (this.isWriteSuccess) {
            // データ書込み成功後の場合、処理結果に関わらず、実行監視画面に遷移する。
            this.phase = PHASE.EXECUTION;
            // 実行監視画面処理
            await this.executionLogic();
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 実行監視画面処理 */
    async executionLogic() {
      // 指定時間待機（監視間隔の時間分カウント）
      this.isLoading = true;
      await util.sleep(this.monitoringIntervalTime);
      // 指定時間待機後、データ読出し処理に遷移
      await this.read();
    },

    /** データ読出し処理 */
    async read() {
      try {
        this.isLoading = true;
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();

        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          async (errorType) => { errorType; await this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          async (result) => { await this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          async (result) => { await this.validateErrorResult(result); },
        );

        if (!response) {
          // pollingWspApiはエラーコールバックの終了を待たずnullを返す
          // 非同期処理を含んでいるshowUnexpectedErrorが呼ばれた場合、スピナー消えてしまうためPromiseの解決を待つ
          if (this.exitPromise) await this.exitPromise;
          return;
        }

        // 正常コールバック
        if (response.data?.status === REQUEST_STATUS_COMPLETED) {
          // 停止画面に遷移
          this.phase = PHASE.SUCCESS;
        } else {
          // 応答値エラーまたは指定時間経過エラーの場合
          switch (response.data?.async_code) {
            case error.WSP_156_READ_ERROR:
              // 読出し結果(HEX値)を0x付きで表示
              this.readValue = response.data?.user_data?.read_value ? `0x${response.data.user_data.read_value.toUpperCase()}` : '';
              // エラー画面に遷移
              this.phase = PHASE.FAILED;
              break;
            default:
              // 共通エラー
              await this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** dispose処理 */
    async dispose() {
      try {
        this.isLoading = true;
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();

        return await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'dispose'
          },
          // エラー発生のコールバックを実装
          undefined,
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          async (result) => { await this.validateErrorResult(result, 'dispose'); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          async (result) => { await this.validateErrorResult(result, 'dispose'); },
        );

      } finally {
        this.isLoading = false;
      }
    },

    /** 機能終了処理 */
    async exit() {
      await this.dispose();
      this.closeWizard();
    },
  },

  /**
   * DOM作成後実行処理
   */
  async mounted() {
    this.init();
  },
};